import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  Limits as LimitsDto,
  LimitScope,
  OrganizationLimits,
  OrganizationLimitType,
} from "../gql/graphql";
import { useMemo } from "react";
import { abbreviateNumber, formatBytes } from "../utils/numbers";

export type LimitStatus = "ok" | "warning" | "critical";

interface BaseLimitInfo {
  id: string;
  name: string;
  description: string;
  type: OrganizationLimitType;
  default: number;
  max: number;
}

export interface OrganizationLimitInfo extends BaseLimitInfo {
  scope: LimitScope.Organization;
  current: number;
  pct: number;
  status: LimitStatus;
}

export interface ResourceLimitInfo extends BaseLimitInfo {
  scope: LimitScope.Resource;
}

export type LimitInfo = OrganizationLimitInfo | ResourceLimitInfo;

export interface LimitsSummary {
  total: number;
  healthy: number;
  nearLimit: number;
  atCapacity: number;
}

export const isResourceScopedLimit = (
  limit: LimitInfo,
): limit is ResourceLimitInfo => limit.scope === LimitScope.Resource;

export const isOrganizationLimit = (
  limit: LimitInfo,
): limit is OrganizationLimitInfo => limit.scope === LimitScope.Organization;

export const isOrganizationUsageLimit = (
  limit: LimitInfo,
): limit is OrganizationLimitInfo =>
  limit.scope === LimitScope.Organization &&
  limit.type !== OrganizationLimitType.Boolean &&
  limit.type !== OrganizationLimitType.Days;

export const getProgressColor = (pct: number): string => {
  if (pct >= 95) return "rose";
  if (pct >= 80) return "amber";
  if (pct >= 50) return "cyan";
  return "teal";
};

export const formatLimitValue = (
  value: number,
  type: OrganizationLimitType,
): string => {
  if (type === OrganizationLimitType.Bytes) return formatBytes(value);
  if (type === OrganizationLimitType.Boolean)
    return value === 1 ? "Enabled" : "Disabled";
  if (type === OrganizationLimitType.Days)
    return `${value} day${value !== 1 ? "s" : ""}`;
  return abbreviateNumber(value);
};

export const formatLimitUsage = (limit: OrganizationLimitInfo): string =>
  `${formatLimitValue(limit.current, limit.type)} / ${formatLimitValue(
    limit.max,
    limit.type,
  )}`;

export const summarizeLimits = (limits: LimitInfo[]): LimitsSummary => {
  const usageLimits = limits.filter(isOrganizationUsageLimit);
  return {
    total: limits.length,
    healthy: usageLimits.filter((l) => l.pct < 80).length,
    nearLimit: usageLimits.filter((l) => l.pct >= 80 && l.pct < 95).length,
    atCapacity: usageLimits.filter((l) => l.pct >= 95).length,
  };
};

const LIMITS_QUERY = gql`
  query limitsHook {
    limitDefinitions {
      limits {
        id
        description
        name
        type
        default
        scope
      }
    }
    organizationLimits {
      limits {
        current
        id
        max
      }
    }
  }
`;

export function useOrganizationLimits(limitIds?: string[]) {
  const { data, loading, error } = useQuery<{
    limitDefinitions: LimitsDto;
    organizationLimits: OrganizationLimits;
  }>(LIMITS_QUERY);

  const limitIdsKey = limitIds && limitIds.length > 0 ? limitIds.join(",") : "";

  const limits: LimitInfo[] = useMemo(() => {
    if (!data) return [];

    const orgMap = new Map(
      data.organizationLimits.limits.map((l) => [l.id, l]),
    );

    let defs = data.limitDefinitions.limits;
    if (limitIdsKey) {
      const idSet = new Set(limitIdsKey.split(","));
      defs = defs.filter((d) => idSet.has(d.id));
    }

    return defs.map((def) => {
      const org = orgMap.get(def.id);
      const max = org?.max ?? def.default;
      const base: BaseLimitInfo = {
        id: def.id,
        name: def.name,
        description: def.description,
        type: def.type,
        default: def.default,
        max,
      };

      if (def.scope === LimitScope.Resource) {
        return { ...base, scope: LimitScope.Resource };
      }

      const current = org?.current ?? 0;
      const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0;
      const status: LimitStatus =
        pct >= 95 ? "critical" : pct >= 80 ? "warning" : "ok";

      return { ...base, scope: LimitScope.Organization, current, pct, status };
    });
  }, [data, limitIdsKey]);

  const summary = useMemo(() => summarizeLimits(limits), [limits]);

  const limitsById = useMemo(
    () => new Map(limits.map((l) => [l.id, l])),
    [limits],
  );

  const getLimit = useMemo(
    () => (id: string): LimitInfo | undefined => limitsById.get(id),
    [limitsById],
  );

  const getOrganizationLimit = useMemo(
    () =>
      (id: string): OrganizationLimitInfo | undefined => {
        const limit = limitsById.get(id);
        return limit && isOrganizationLimit(limit) ? limit : undefined;
      },
    [limitsById],
  );

  return { limits, summary, loading, error, getLimit, getOrganizationLimit };
}
